//Imports
import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";

//Hooks
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";

//Actions
import { reset, manageSearchTemperatures } from "./redux/slice";
import { sendGenericExport, clearRedux, savePreference } from "Redux/actions";
import { openModalCreateEditManageTemperature } from "./modals/createEditManageTemperature/redux/slice";
import { openModalDeleteManageTemperature } from "./modals/deleteManageTemperature/redux/slice";
import { validateJsonObject } from "Core/data/Helpers";
import { HasPermission } from "Core/security"

//Controls
import {
  Container,
  IconButton,
  MenuComponent,
  SearchInput,
  TableButtons,
  TableView,
  Tooltip,
  useTablePagination
} from "@gpstrackit/gtc-react-controls";

//Components
import { Grid } from "@mui/material";
import { NotificationManager } from "../../../../components/Notifications";
import Preferences from "Core/containers/Preferences";

//Modals
import CreateEditManageTemperature from "./modals/createEditManageTemperature";
import DeleteManageTemperature from "./modals/deleteManageTemperature";

//Helpers
import { completeColumns } from "Core/data/Helpers";
import { disableItemSelection } from "../../.globals/utils";
const KEY_PREFERENCE = "Manage-TemperatureTab-list";
const INITIAL_SORT = { key: "label", order: "desc" };

const getNormalizedTemperatureAlerts = (row) =>
  row?.temperatureAlerts?.map((temperatureAlert) => ({
    ...temperatureAlert,
    config: validateJsonObject(temperatureAlert?.configJSON) || {}
  })) || [];

const getZoneSensor = (row, zone) => {
  const temperatureSensors = getNormalizedTemperatureAlerts(row);
  return temperatureSensors.find((sensor) => sensor?.config?.zone === zone) || null;
};

const getZoneName = (row, zone) => {
  const temperatureSensor = getZoneSensor(row, zone);
  if (!temperatureSensor) return "";

  let name = temperatureSensor?.name || "";
  if (row?.hid) {
    name = name.replaceAll(` {${row.hid}}`, "");
  }
  if (temperatureSensor?.config?.zone !== undefined) {
    name = name.replaceAll(` {${temperatureSensor.config.zone}}`, "");
  }

  return name.split("{")[0] || "";
};

const getZoneColumn = (zone, messages) => ({
  key: `zone${zone}`,
  title: `${messages["manage_temperatures_zone_label"]} ${zone}`, 
  show: true,
  export: true,
  functionString: `
    (function () {
      try {
        const alerts = (row.temperatureAlerts || []).map(function (temperatureAlert) {
          var cfg = {};
          if (temperatureAlert && temperatureAlert.configJSON) {
            try {
              cfg = JSON.parse(temperatureAlert.configJSON) || {};
            } catch (e) {
              cfg = {};
            }
          }
          return Object.assign({}, temperatureAlert, { config: cfg });
        });
        var sensor = alerts.find(function (s) { return s && s.config && s.config.zone === ${zone}; });
        if (!sensor || !sensor.name) return "";
        var name = sensor.name;
        if (row && row.hid) {
          name = name.replaceAll(" {" + row.hid + "}", "");
        }
        if (sensor.config && sensor.config.zone !== undefined) {
          name = name.replaceAll(" {" + sensor.config.zone + "}", "");
        }
        return (name.split("{")[0] || "");
      } catch (e) {
        return "";
      }
    })()
  `,
  exportValue: (row) => { 
    return getZoneName(row, zone);
  },
  render: (row) => {
    const temperatureSensor = getZoneSensor(row, zone);
    const name = getZoneName(row, zone);
    return (
      <Grid
        container
        alignItems="center"
        justifyContent="space-between"
        className="context-menu-container"
        wrap="nowrap"
      >
        <Tooltip
          title={
            <React.Fragment>
              <b>{messages["manage_temperatures_zone_name"]}</b>: {name}
              <br />
              <b>{messages["manage_temperatures_zone_high"]}</b>: {temperatureSensor?.config?.high}{" "}
              <br />
              <b>{messages["manage_temperatures_zone_low"]}</b>: {temperatureSensor?.config?.low}
            </React.Fragment>
          }
          placement={"top"}
        >
          <p noWrap>{name}</p>
        </Tooltip>
      </Grid>
    );
  }
});

const TemperatureTab = () => {
  //States
  const [selectedItems, setSelectedItems] = useState([]);
  const [preferences, setPreferences] = useState({});
  const [loadPreferences, setLoadPreferences] = useState(true);
  const [loadColumns, setLoadColumns] = useState(false);

  //Table refs
  const unitsTableRef = useRef();

  // Permissions
  const user = useSelector((state) => state.securityRedux.user);
  const editablePermissions = HasPermission(user, "", [345])

  //Language
  const { messages, locale } = useIntl();

  //Redux
  const authenticatedId = useSelector((state) => state.securityRedux.authenticatedId);
  const { loadingTemperatures, temperatures, totalTemperatures, error } = useSelector(
    (state) => state.manageTemperatureTabRedux
  );
  const openCreateEditManageTemperature = useSelector(
    (state) => state.manageCreateEditManageTemperatureRedux.open
  );
  const openDeleteManageTemperature = useSelector(
    (state) => state.manageDeleteManageTemperatureRedux.open
  );
  const exportResult = useSelector((state) => state.commomRedux.exportResult);
  const exportName = useSelector((state) => state.commomRedux.exportName);
  const dispatch = useDispatch();
  const paginationUnits = useTablePagination({
    dispatch,
    action: manageSearchTemperatures,
    exportName: "temperatureAlerts",
    searchFields: ["label"],
    sendGenericExport,
    tableRef: unitsTableRef
  });

  //Options
  const menuTemperatureTabOptions = [messages["edit"], messages["delete"]];

  //Memos
  const currentColumns = useMemo(() => {
    const zoneColumns = [];
    for (let i = 1; i <= 3; i++) {
      zoneColumns.push(getZoneColumn(i, messages));
    }
    return [
      {
        key: "label",
        title: messages["manage_temperatures_label"],
        functionString: 'return row?.label || ""',
        show: true,
        export: true,
        sort: true,
        exportValue: (row) => row?.label,
        render: (row) => (
          <Grid container alignItems="center">
            <Grid item>
              {row?.label.length > 27 ? (
                <Tooltip title={row.label}>
                  <p>{row.label.slice(0,27)}...</p>
                </Tooltip>
              ) : (
                <p>{row.label}</p>
              )}
            </Grid>
            {editablePermissions &&
              <Grid item>
                <MenuComponent
                  placement="right-start"
                  options={menuTemperatureTabOptions}
                  icon="more_vert"
                  className="showInHover"
                  onClick={handleClickMenu}
                  value={row}
                />
              </Grid>
            }
          </Grid>
        )
      },
      ...zoneColumns
    ];
  }, [locale]);

  //Handlers and utils
  const handleClickMenu = (type, temperature) => {
    if (type === messages["edit"]) {
      dispatch(openModalCreateEditManageTemperature(temperature));
      return;
    }
    if (type === messages["delete"]) {
      if (temperature.id)
        dispatch(openModalDeleteManageTemperature({ deleteTemperatureList: [temperature] }));
      return;
    }
  };

  const handleDeleteTemperatureList = useCallback(() => {
    dispatch(openModalDeleteManageTemperature({ deleteTemperatureList: selectedItems }));
  }, [selectedItems]);

  const savePreferences = useCallback(
    (preferencesArray) => {
      if (!Array.isArray(preferencesArray)) {
        console.error("Expected an array of preferences");
        return;
      }

      let updatedPreferences = { ...preferences } || {};

      preferencesArray.forEach(({ key, value }) => {
        updatedPreferences[key] = value;
      });

      setPreferences(updatedPreferences);

      dispatch(
        savePreference({
          entityName: "User",
          entityId: authenticatedId,
          preferenceKey: KEY_PREFERENCE,
          preferenceValue: updatedPreferences
        })
      );
    },
    [preferences]
  );

  //Effects
  useEffect(() => {
    if (preferences?.columns?.length && !loadColumns) {
      const preferenceMap = new Map(preferences.columns.map((col) => [col.key, col]));

      // Reorder columns based on preferences (keeping `show`)
      const reorderedColumns = preferences.columns
        .map((pref) => {
          const col = currentColumns.find((c) => c.key === pref.key);
          return col ? { ...col, show: pref.show } : null;
        })
        .filter(Boolean);

      // Add new columns (not listed in preferences), with `show: true` by default
      const newColumns = currentColumns
        .filter((col) => !preferenceMap.has(col.key))
        .map((col) => ({ ...col, show: true }));

      const finalColumns = [...reorderedColumns, ...newColumns];

      paginationUnits.setColumns(finalColumns);
      setPreferences({ ...preferences });
      setLoadColumns(true);
    }
  }, [preferences.columns, loadColumns]);

  useEffect(() => {
    if (paginationUnits.columns.length > 0) {
      setPreferences({ ...preferences, columns: paginationUnits.columns });
      setLoadColumns(false);
    }
  }, [locale]);

  useEffect(() => {
    paginationUnits.setColumns(currentColumns);
  }, [currentColumns]);

  useEffect(() => {
    if (!error) return;
    NotificationManager.error(
      messages["errorMessage"],
      messages["error"],
      3000,
      null,
      null,
      "warning"
    );
    dispatch(reset({ error: null }));
  }, [error]);

  useEffect(() => {
    if (!exportResult || exportName !== "temperatureAlerts") return;

    NotificationManager.info(
      messages["exportInProcessBody"],
      messages["exportInProcessTitle"],
      4000,
      null,
      null,
      "info",
      null
    );
    dispatch(clearRedux("COMMON", { exportResult: null, exportName: null }));
  }, [exportResult, exportName]);
  return (
    <Fragment>
      <Container maxWidth={false}>
        <Grid container direction="row" justifyContent="space-between" alignItems="center">
          <Grid item xs={6} sm={2}>
            <Grid
              container
              direction="row"
              justifyContent="flex-start"
              alignItems="center"
              spacing={1}
            >
              <Grid item style={{ display: "flex" }}>
                {editablePermissions &&
                  <>
                    <IconButton
                      tooltip={messages["add"]}
                      placement="bottom"
                      variant="secondary"
                      icon="add_box"
                      size="small"
                      style={{ opacity: loadingTemperatures ? "0.6" : "1" }}
                      isDisabled={loadingTemperatures}
                      onClick={() => dispatch(openModalCreateEditManageTemperature())}
                    />
                    <IconButton
                      tooltip={messages["delete"]}
                      placement="bottom"
                      variant="secondary"
                      icon="delete"
                      size="small"
                      onClick={() => handleDeleteTemperatureList()}
                      style={{ opacity: !selectedItems.length && "0.6" }}
                      isDisabled={!selectedItems.length}
                    />
                  </>
                }
                <TableButtons
                  messages={messages}
                  buttons={["export", "orderColumn"]}
                  {...paginationUnits}
                  tableRef={unitsTableRef}
                  columns={paginationUnits.columns?.filter(col => col.exportOnly !== true)}
                  onExport={(type) => {
                    const tableRef = unitsTableRef?.current;
                    if (!tableRef) return;

                    if (type === "Csv") {
                      tableRef.exportCSV();
                    } else if (type === "Pdf") {
                      tableRef.exportPDF();
                    }
                  }}
                  onChangeColumns={(columns) => {
                    const baseColumns = currentColumns || [];
                    const finalsColumns = completeColumns(baseColumns, columns);
                    savePreferences([{ key: "columns", value: finalsColumns }]);
                    paginationUnits.setColumns(finalsColumns);
                  }}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} sm={4} className="mb-10 text-right">
            <SearchInput
              placeholder={messages["manage_temperatures_search_by_vehicle"]}
              searchText={paginationUnits.searchText}
              onSearchText={paginationUnits.onSearchChange}
            />
          </Grid>
        </Grid>
        <Preferences
          preferencesKey={KEY_PREFERENCE}
          onInitialPreferences={(data) => {
            if (!loadPreferences) return;
            setTimeout(() => {
              let sort = data?.sort || INITIAL_SORT; // * Initial sort
              unitsTableRef.current?.setSort?.(sort);
              paginationUnits.onChangeSort(sort);
            }, 100);
            setPreferences(data);
            setLoadPreferences(false);
          }}
        >
          <TableView
            keyColumn="id"
            loading={loadingTemperatures || loadPreferences}
            data={temperatures}
            disableItemSelection={disableItemSelection(paginationUnits)}
            total={totalTemperatures}
            topheight={235}
            exportOrientation="landscape"
            onSelectedItems={(items) => setSelectedItems(items)}
            {...paginationUnits}
            onChangeColumns={(columns) => {
              const baseColumns = currentColumns || [];
              const finalsColumns = completeColumns(baseColumns, columns);
              savePreferences([{ key: "columns", value: finalsColumns }]);
              paginationUnits.onChangeColumns(finalsColumns);
            }}
            onChangeSort={(newSort) => {
              savePreferences([{ key: "sort", value: newSort }]);
              paginationUnits.onChangeSort(newSort);
            }}
          />
        </Preferences>
      </Container>
      {openCreateEditManageTemperature && (
        <CreateEditManageTemperature refresh={unitsTableRef?.current?.refreshTable} />
      )}
      {openDeleteManageTemperature && (
        <DeleteManageTemperature refresh={() => {
          paginationUnits.onSearchChange("");
        }} />
      )}
    </Fragment>
  );
};

export default TemperatureTab;
